import React, { useCallback, useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import { RightOutlined } from '@ant-design/icons';
import { Table as AntdTable, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import Truncate from 'v2/components/Shared/TruncateText';
import type { IAffiliateCommission } from 'v2/interfaces/reports/affiliateCommissions';
import { affiliateCommissionsService } from 'v2/services/reports/affiliateCommissions';

interface IProps {
  handleSeeDetailsClick: (affiliate_id: number) => void;
}

export const Table = ({ handleSeeDetailsClick }: IProps) => {
  useObservable(() => affiliateCommissionsService.loadCommissions(), []);
  const [commissions] = useObservable(() => affiliateCommissionsService.getCommissions(), []);
  const [isLoading] = useObservable(() => affiliateCommissionsService.isLoading(), []);
  const [pagination] = useObservable(() => affiliateCommissionsService.getPaginationResponse(), []);

  const onChangePerPage = useCallback(
    (perPage: number) => {
      affiliateCommissionsService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
      document.getElementById('commissions-affiliates-table')?.scrollIntoView();
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      affiliateCommissionsService.setPagination({ ...pagination, current_page: page });
      document.getElementById('commissions-affiliates-table')?.scrollIntoView();
    },
    [pagination]
  );

  const columns: ColumnsType<IAffiliateCommission> = useMemo(
    () => [
      {
        title: 'Afiliado',
        key: 'affiliate',
        width: 440,
        render: (_: unknown, commission) => {
          if (!commission?.name) {
            return <span>-</span>;
          }

          return (
            <>
              <span className='block'>
                <Truncate count={25} final='...'>
                  <a
                    href={`https://app.selectafiliados.com.br/affiliate/detailsSource/${commission.id}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {commission.name}
                  </a>
                </Truncate>
              </span>
              <span className='block'>
                <Truncate count={25} final='...'>
                  {commission.email || ''}
                </Truncate>
              </span>
            </>
          );
        }
      },
      {
        title: 'Quantidade de Comissões',
        dataIndex: 'total_commissions',
        key: 'total_commissions',
        width: 400,
        render: (value: number) => <span className='text-xs'>{value ?? 0}</span>
      },
      {
        title: 'Total de comissão paga (líquido)',
        dataIndex: 'total_gain',
        key: 'total_gain',
        width: 300,
        align: 'right',
        render: (value: number) => (
          <span className='font-semibold'>
            <FormattedNumber value={value ?? 0} format='BRL' />
          </span>
        )
      },
      {
        title: '',
        key: 'actions',
        width: 240,
        fixed: 'right',
        align: 'right',
        render: (_: unknown, commission) => (
          <Button type='text' icon={<RightOutlined />} onClick={() => handleSeeDetailsClick(commission.id)} />
        )
      }
    ],
    [handleSeeDetailsClick]
  );

  return (
    <div id='commissions-affiliates-table' data-testid='commissions-affiliates-table' className='overflow-x-auto'>
      <AntdTable<IAffiliateCommission>
        rowKey='id'
        columns={columns}
        dataSource={commissions || []}
        loading={!!isLoading}
        pagination={{
          current: pagination?.current_page || 1,
          total: pagination?.total_items || 0,
          pageSize: pagination?.per_page || 10,
          pageSizeOptions: ['10', '25', '50'],
          showSizeChanger: true,
          onChange: page => onChangePage(page),
          onShowSizeChange: (_, size) => onChangePerPage(size)
        }}
        locale={{ emptyText: 'Nenhum registro encontrado.' }}
      />
    </div>
  );
};
